import {
  anyContainingPermission,
  useAuthorizationRequired,
} from "../auth/WithAuthorizationRequired";
import { AnyRobotSummaryCardProps } from "../robots/AnyRobotSummaryCard";
import { atkEquipmentApi } from "common/state/portalApi/atkEquipment";
import { buildPermission } from "common/utils/auth";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  OutlinedInput,
  Select,
  Tooltip,
} from "@mui/material";
import { capitalize } from "portal/utils/strings";
import { classes } from "common/theme/theme";
import { ConfigNode, ConfigType } from "protos/config/api/config_service";
import { farmApi } from "common/state/portalApi/farm";
import { getConfigNode, getNodeFromPath, getValue } from "portal/utils/configs";
import {
  getCreateTractorPath,
  getEditTractorPath,
  getSwitchRigPath,
} from "portal/utils/routing";
import { getCustomerStatus, getVersionStatus } from "../robots/RobotStatus";
import { MuiColor } from "common/hooks/useMuiColor";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { portalApi } from "portal/state/portalApi";
import { RobotSummaryCard } from "../robots/RobotSummaryCard";
import { RobotSummaryResponse } from "protos/portal/robots";
import { SummaryCardEditButtonDialog } from "../SummaryCardEditDialogButton";
import { SummaryCardItemProps } from "../SummaryCardItem";
import { useIntersectionObserver } from "@uidotdev/usehooks";
import {
  useLazyPopups,
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useMemoAsync } from "portal/utils/hooks/useMemoAsync";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import EditIcon from "@mui/icons-material/EditOutlined";
import FarmIcon from "portal/images/icons/farm.svg?react";
import PendingIcon from "@mui/icons-material/PendingOutlined";
import React, { FunctionComponent, useMemo, useState } from "react";
import RigIcon from "@mui/icons-material/SettingsOutlined";
import SaveIcon from "@mui/icons-material/SaveOutlined";
import SwitchIcon from "@mui/icons-material/SwapHorizOutlined";
import TractorIcon from "@mui/icons-material/AgricultureOutlined";

const ACTIVE_FARM_ID_PATH = "rtc/tractor_ctl/active_farm_id";

const EditActiveFarmActionButton: React.FC<{
  serial: string;
  activeFarmId: string;
  className?: string;
  color?: MuiColor;
}> = ({ serial, activeFarmId, className, color }) => {
  const { t } = useTranslation();
  const { data: farms, isLoading } = useQueryPopups(
    farmApi.endpoints.listFarms.useQuery({ contents: "none" })
  );
  const [selectedId, setSelectedId] = useState(activeFarmId);
  const [setConfigValue] = useMutationPopups(
    portalApi.endpoints.setConfigValue.useMutation()
  );

  async function changeActiveFarm(close: () => void): Promise<void> {
    await setConfigValue({
      serial,
      path: ACTIVE_FARM_ID_PATH,
      value: selectedId,
      reason: "change farm",
      configType: ConfigType.STRING,
    });
    close();
  }

  const farmLabel = capitalize(t("models.farms.farm", { count: 1 }));

  return (
    <SummaryCardEditButtonDialog
      buttonTooltip={t("views.autotractor.robotSummary.changeActiveFarmTitle")}
      color={color}
      iconClassName={classes("p-0", className)}
      IconComponent={EditIcon}
    >
      {({ close }) => (
        <>
          <DialogTitle>
            {t("views.autotractor.robotSummary.changeActiveFarmTitle")}
          </DialogTitle>
          <DialogContent>
            <FormControl fullWidth>
              <InputLabel>{farmLabel}</InputLabel>
              <Select<typeof selectedId>
                input={<OutlinedInput label={farmLabel} />}
                defaultValue={selectedId}
                value={selectedId}
                label={farmLabel}
                onChange={(e) => setSelectedId(e.target.value)}
              >
                {farms?.map((farm) => (
                  <MenuItem value={farm.id?.id} key={farm.id?.id}>
                    {farm.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button variant="text" onClick={close}>
              {t("utils.actions.cancel")}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              loading={isLoading}
              startIcon={<SaveIcon />}
              onClick={() => changeActiveFarm(close)}
            >
              {t("utils.actions.save")}
            </Button>
          </DialogActions>
        </>
      )}
    </SummaryCardEditButtonDialog>
  );
};

function useCurrentFarmStatus(
  serial: string,
  config: ConfigNode | undefined
): SummaryCardItemProps {
  const { t } = useTranslation();
  const activeFarmId =
    getValue(getNodeFromPath(config, ACTIVE_FARM_ID_PATH))?.toString() ?? "";
  const { data: activeFarm, isLoading } = useQueryPopups(
    farmApi.endpoints.getFarm.useQuery(activeFarmId, {
      skip: !activeFarmId,
    })
  );

  const canChangeConfig = useAuthorizationRequired({
    or: [
      buildPermission(
        PermissionAction.update,
        PermissionResource.configs,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.update,
        PermissionResource.configs,
        PermissionDomain.all
      ),
    ],
  });
  const actions: JSX.Element[] = [];

  let currentFarmText: string;
  let color: MuiColor | undefined;

  if (!activeFarmId) {
    currentFarmText = t("views.autotractor.robotSummary.noActiveFarm");
    color = "warning";
  } else if (isLoading) {
    currentFarmText = t("components.Loading.placeholder");
  } else if (activeFarm) {
    currentFarmText = t("views.autotractor.robotSummary.activeFarm", {
      farmName: activeFarm.name,
    });
  } else {
    currentFarmText = t(
      "views.autotractor.robotSummary.loadingActiveFarmFailed",
      {
        farmId: activeFarmId,
      }
    );
    color = "warning";
  }

  if (canChangeConfig && config) {
    actions.push(
      <EditActiveFarmActionButton
        serial={serial}
        activeFarmId={activeFarmId}
        color={color}
      />
    );
  }

  return {
    icon: <FarmIcon />,
    text: currentFarmText,
    color,
    actions,
    isEmpty: false,
    hideEmpty: false,
  };
}

function useDefaultTractorStatus(
  serial: string,
  summary: RobotSummaryResponse | undefined
): SummaryCardItemProps {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tractorId = summary?.robot?.defaultAtkTractorId ?? "";
  const { data: tractorsForCustomer, isLoading } = useQueryPopups(
    atkEquipmentApi.endpoints.searchTractors.useQuery(
      {
        customerId: summary?.customer?.db?.id
          ? summary.customer.db.id.toString()
          : "",
      },
      { skip: !summary?.customer?.db?.id }
    )
  );
  const [setDefaultTractor] = useMutationPopups(
    portalApi.endpoints.setDefaultAtkTractor.useMutation()
  );
  const [clearDefaultTractor] = useMutationPopups(
    portalApi.endpoints.clearDefaultAtkTractor.useMutation()
  );
  const tractorsById = useMemo(
    () =>
      tractorsForCustomer
        ? Object.fromEntries(
            tractorsForCustomer.tractors.map((tractor) => [tractor.id, tractor])
          )
        : {},
    [tractorsForCustomer]
  );
  const canEditTractor = useAuthorizationRequired(
    anyContainingPermission(
      PermissionAction.update,
      PermissionResource.atk_equipment,
      PermissionDomain.customer
    )
  );
  // XXX: this should be changed to a different permission per SOFTWARE-5213.
  const canSwitchDefaultTractor = useAuthorizationRequired(
    anyContainingPermission(
      PermissionAction.update,
      PermissionResource.atk_equipment,
      PermissionDomain.all
    )
  );
  const [switchDialogOpen, setSwitchDialogOpen] = useState(false);
  const [selectedTractorId, setSelectedTractorId] = useState(tractorId);

  let currentRigText: string;
  let showEditButton = false;
  let color: MuiColor | undefined;

  if (!tractorId) {
    currentRigText = t("views.autotractor.robotSummary.noDefaultTractor");
    color = "error";
  } else if (isLoading) {
    currentRigText = t("components.Loading.placeholder");
  } else if (tractorsById[tractorId]) {
    currentRigText = t("views.autotractor.robotSummary.defaultTractor", {
      tractorName: tractorsById[tractorId].name,
    });
    showEditButton = true;
  } else {
    currentRigText = t(
      "views.autotractor.robotSummary.loadingDefaultTractorFailed",
      { tractorId }
    );
    color = "error";
  }

  return {
    icon: <TractorIcon />,
    text: currentRigText,
    color,
    actions: [
      canSwitchDefaultTractor ? (
        <>
          <Dialog open={switchDialogOpen}>
            <DialogTitle>
              {t("views.autotractor.robotSummary.switchTractor")}
            </DialogTitle>
            <DialogContent className="flex flex-col gap-2">
              <Select<string>
                onChange={(e) => setSelectedTractorId(e.target.value)}
                value={selectedTractorId}
                displayEmpty={true}
              >
                <MenuItem value="" key="none">
                  <em>{t("utils.descriptors.none")}</em>
                </MenuItem>
                {Object.values(tractorsById).map((tractor) => (
                  <MenuItem value={tractor.id} key={tractor.id}>
                    {tractor.name}
                  </MenuItem>
                ))}
              </Select>
            </DialogContent>
            {isLoading && <LinearProgress variant="indeterminate" />}
            <DialogActions>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setSwitchDialogOpen(false)}
              >
                {t("utils.actions.cancel")}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => navigate(getCreateTractorPath(serial))}
              >
                {t("utils.actions.create")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={selectedTractorId === tractorId}
                onClick={async () => {
                  if (selectedTractorId) {
                    const res = await setDefaultTractor({
                      serial,
                      tractorId: selectedTractorId,
                    });
                    if (!res.error) {
                      setSwitchDialogOpen(false);
                    }
                  } else {
                    const res = await clearDefaultTractor({ serial });
                    if (!res.error) {
                      setSwitchDialogOpen(false);
                    }
                  }
                }}
              >
                {t("utils.actions.selectShort")}
              </Button>
            </DialogActions>
          </Dialog>
          <Tooltip
            title={t("views.autotractor.robotSummary.switchTractor")}
            arrow
            key="switch"
          >
            <IconButton
              color={color}
              className="p-0"
              size="medium"
              onClick={() => {
                setSelectedTractorId(tractorId);
                setSwitchDialogOpen(true);
              }}
            >
              <SwitchIcon style={{ color: "inherit", fontSize: "inherit" }} />
            </IconButton>
          </Tooltip>
        </>
      ) : (
        <></>
      ),
      canEditTractor && showEditButton ? (
        <Tooltip
          title={t("views.autotractor.robotSummary.editTractor")}
          arrow
          key="edit"
        >
          <IconButton
            color={color}
            className="p-0"
            size="medium"
            onClick={() => navigate(getEditTractorPath(tractorId))}
          >
            <EditIcon style={{ color: "inherit", fontSize: "inherit" }} />
          </IconButton>
        </Tooltip>
      ) : (
        <></>
      ),
    ],
    isEmpty: !tractorId,
    hideEmpty: false,
  };
}

function useCurrentRigStatus(
  serial: string,
  summary: RobotSummaryResponse | undefined
): SummaryCardItemProps {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const rigId = summary?.robot?.activeAtkRigId;
  const { data: searchResults, isLoading } = useQueryPopups(
    atkEquipmentApi.endpoints.searchRigs.useQuery(
      {
        rigId,
        validate: true,
      },
      { skip: !rigId }
    )
  );
  // XXX: this should be changed to a different permission per SOFTWARE-5213.
  const canSwitchRig = useAuthorizationRequired(
    anyContainingPermission(
      PermissionAction.update,
      PermissionResource.atk_equipment,
      PermissionDomain.customer
    )
  );

  let currentRigText: string;
  let color: MuiColor | undefined;

  if (!rigId) {
    currentRigText = t("views.autotractor.robotSummary.noActiveRig");
    color = "warning";
  } else if (isLoading) {
    currentRigText = t("components.Loading.placeholder");
  } else if (searchResults && searchResults.rigs.length > 0) {
    currentRigText = t("views.autotractor.robotSummary.activeRig", {
      rigName: searchResults.rigs[0]?.customerProvidedName,
    });
  } else {
    currentRigText = t(
      "views.autotractor.robotSummary.loadingActiveRigFailed",
      { rigId }
    );
    color = "warning";
  }

  return {
    icon: <RigIcon />,
    text: currentRigText,
    color,
    actions: canSwitchRig
      ? [
          <Tooltip title={"Switch rig"} arrow key="edit">
            <IconButton
              className="p-0"
              color={color}
              size="medium"
              onClick={() => navigate(getSwitchRigPath(serial))}
            >
              <EditIcon style={{ color: "inherit", fontSize: "inherit" }} />
            </IconButton>
          </Tooltip>,
        ]
      : [],
    isEmpty: !rigId,
    hideEmpty: false,
  };
}

export const AtkSummaryCard: FunctionComponent<AnyRobotSummaryCardProps> = ({
  variant,
  serial,
  summary,
}) => {
  const { t } = useTranslation();
  // only load config if we're on screen
  const [ref, intersection] = useIntersectionObserver();
  const isOnScreen = intersection?.isIntersecting ?? false;

  const [getConfig] = useLazyPopups(
    portalApi.endpoints.getConfig.useLazyQuery()
  );
  const [config] = useMemoAsync(
    async () => {
      if (summary?.config) {
        return getConfigNode(summary.config);
      }
      if (!isOnScreen) {
        return;
      }
      const { data } = await getConfig({ serial }, true);
      return data?.config ? getConfigNode(data.config) : undefined;
    },
    [getConfig, isOnScreen, serial, summary?.config],
    summary?.config
  );

  const canReadCustomers = useAuthorizationRequired(
    buildPermission(
      PermissionAction.read,
      PermissionResource.customers,
      PermissionDomain.all
    )
  );
  const canUpdateCustomers = useAuthorizationRequired(
    buildPermission(
      PermissionAction.update,
      PermissionResource.customers,
      PermissionDomain.all
    )
  );
  const farmStatus = useCurrentFarmStatus(serial, config);
  const tractorStatus = useDefaultTractorStatus(serial, summary);
  const rigStatus = useCurrentRigStatus(serial, summary);
  const customerStatus = getCustomerStatus(
    summary,
    canReadCustomers,
    !canUpdateCustomers
  );
  const canReadHardware = useAuthorizationRequired(
    anyContainingPermission(
      PermissionAction.read,
      PermissionResource.hardware,
      PermissionDomain.customer
    )
  );
  const versionStatus = getVersionStatus(t, summary, canReadHardware);

  const summarySections = useMemo(() => {
    const software: SummaryCardItemProps[] = [versionStatus];
    const operations: SummaryCardItemProps[] = [
      farmStatus,
      tractorStatus,
      rigStatus,
    ];
    const management: SummaryCardItemProps[] = [];

    if (canReadCustomers && customerStatus) {
      management.push(customerStatus);
    }

    const result: {
      label: string;
      items: SummaryCardItemProps[];
    }[] = [
      {
        label: t("views.fleet.robots.summary.sections.software"),
        items: software,
      },
      {
        label: t("views.autotractor.robotSummary.operations"),
        items: operations,
      },
      {
        label: t("views.fleet.robots.summary.sections.management"),
        items: management,
      },
    ];

    return result;
  }, [
    canReadCustomers,
    customerStatus,
    farmStatus,
    rigStatus,
    t,
    tractorStatus,
    versionStatus,
  ]);

  return (
    <RobotSummaryCard
      ref={ref}
      variant={variant}
      priorityStatus={[]}
      summarySections={summarySections}
      serial={serial}
      customerSerial={summary?.robot?.customerSerial}
      headlineInfo={{
        StatusIcon: PendingIcon,
        statusColor: "textDisabled",
        headlineText: "",
        checkedIn: undefined,
      }}
    />
  );
};
